import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { FieldErrors, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuidV4 } from 'uuid';

// COMPONENTS
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import { AreaButton } from '@/main/components/shared/styles';
import ModalPartDetails from '@/main/screens/order/createorder/steps/stepTwo/modalPartDetails/modalPartDetails';

// SERVICES
import { quoteSupplierItemApi } from '@/main/api';
import { ReasonFilterRequest } from '@/main/models/request';
import {
  QuoteHeaderAnswerInformationResponse,
  QuoteSupplierItemImageResponse,
  QuoteSupplierItemPartResponse
} from '@/main/models/response';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { StatusQuoteSupplierItem } from '@/main/services/enum/StatusQuoteSupplierItem';
import { PartTypeEnum } from '@/main/services/enum/PartTypeEnum';
import { getTranslateByKey, isNullUndefinedEmpty } from '@/main/services/utils';
import { userIsSupplier } from '@/main/services/utilsUser';

// REDUX
import KogModalFadeComponent from '@/main/components/modal/ModalFadeComponent';
import ModalImages from '@/main/components/shared/modalImages/modalImages';
import { DefaultValueParameterEnum } from '@/main/services/enum/DefaultValueParameterEnum';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { getBrand, useBrandReducer } from '@/main/store/brand';
import { getPackage, usePackageReducer } from '@/main/store/package';
import { getReason } from '@/main/store/reason';
import { getWarehouse, useWarehouseReducer } from '@/main/store/warehouse';

// SCREENS/COMPONENTS/CONTEXT LOCAL
import useAsyncModalConfirm from '@/main/components/shared/asyncModalConfirm/asyncModalConfirm';
import useKogControlElementId from '@/main/hooks/useKogControlElementId';
import { KeyValueDefault } from '@/main/models/common';
import ModalUploadImage from '@/main/screens/quotation/createAnswer/answeredParts/gridAnswerQuote/TableRowAnswer/modalUploadImage/modalUploadImage';
import ModalFavoriteCategory from '@/main/screens/quotation/createAnswer/answeredParts/modalFavoriteCategory/modalFavoriteCategory';
import { Controls } from '@/main/services/constants/constantsControl';
import { isNil } from 'lodash';
import ModalNationalRequote from './components/modalNationalRequote/modalNationalRequote';
import ModalNotQuoteSubmit from './components/modalNotQuoteSubmit/modalNotQuoteSubmit';
import { createAnswerSchema, IFormValueRow } from './createAnswerSchema';
import GridAnswer from './gridAnswerQuote/gridAnswer';
import GridPending from './gridAnswerQuote/gridPending';
import HeaderAnswer from './headerAnswer/headerAnswer';
import { ICreateAnswerForm, IFormValueRowRoot } from './interfaceCreateAnswer';
import * as S from './styles';
import {
  formatResponseToForm,
  generateRequestSaveCreateAnswer
} from './utilscreateAnswer';

export interface IHandleOpenModalConfirmSetReason {
  reason: KeyValueDefault | null;
  observation?: string | null;
}
export interface IInitialDataModalSetReason extends IHandleOpenModalConfirmSetReason {
  openModalSetReason: boolean;
}
export interface IModalUploadImagesState {
  part: QuoteSupplierItemPartResponse | null;
  isOpen: boolean;
  allowEdit: boolean;
}

export function getLabelTranslation(key: string) {
  const translationObj: { [key: string]: string } = {
    warehouseName: getTranslateByKey('information.warehouse'),
    partTitle: getTranslateByKey('information.name'),
    partTypeName: getTranslateByKey('information.partTypes'),
    partBrandName: getTranslateByKey('information.brand'),
    stock: getTranslateByKey('information.amount'),
    answeredPrice: getTranslateByKey('information.price'),
    pickupTime: getTranslateByKey('information.pickupDaysDeadline'),
    weight: getTranslateByKey('information.weight'),
    packageTypeName: getTranslateByKey('information.packageType'),
    height: getTranslateByKey('information.height'),
    width: getTranslateByKey('information.width'),
    depth: getTranslateByKey('information.depth'),
    reason: getTranslateByKey('information.reason'),
    observation: getTranslateByKey('information.observation')
  };

  return translationObj[key] || key;
}

interface IIndexedPendingItemError {
  index: number;
  error: FieldErrors<IFormValueRow>;
}

function getIndexedPendingItemErrors(
  errors: FieldErrors<ICreateAnswerForm>
): IIndexedPendingItemError[] {
  const pendingItemsErrors = errors.pendingItems;

  if (!Array.isArray(pendingItemsErrors)) {
    return [];
  }

  return pendingItemsErrors.flatMap((error, index) =>
    error ? [{ index, error: error as FieldErrors<IFormValueRow> }] : []
  );
}

const uniqueIdRow = (guidQuoteSupplierItem: string) =>
  `${guidQuoteSupplierItem}-${uuidV4()}`;

interface ICreateAnswerCartsScreen {
  readonly dataHeader: QuoteHeaderAnswerInformationResponse;
  readonly isLoadingHeader: boolean;
  readonly loadHeader: (guidQuote: string) => void;
  readonly titleScreen: string;
  readonly guidQuoteParams: string;
  readonly isFinishedTime: boolean;
  readonly handleFinishTime: () => void;
}

function CreateAnswerCartsScreen({
  dataHeader,
  isLoadingHeader,
  loadHeader,
  titleScreen,
  guidQuoteParams,
  isFinishedTime,
  handleFinishTime
}: ICreateAnswerCartsScreen) {
  // hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();

  const { brand: brandReducer } = useBrandReducer();
  const { package: packageReducer } = usePackageReducer();
  const { warehouse: warehouseReducer } = useWarehouseReducer();

  // states
  const [isOpenModalNotQuoteSubmit, setIsOpenModalNotQuoteSubmit] =
    useState<boolean>(false);
  const [dataGridPending, setDataGridPending] = useState<
    QuoteSupplierItemPartResponse[]
  >([]);
  const [dataGridAnswer, setDataGridAnswer] = useState<
    QuoteSupplierItemPartResponse[]
  >([]);
  const [pendingIsDeliveryBySupplier, setpendingIsDeliveryBySupplier] =
    useState<boolean>(false);
  const [answeredIsDeliveryBySupplier, setansweredIsDeliveryBySupplier] =
    useState<boolean>(false);

  const isDelivery =
    pendingIsDeliveryBySupplier || answeredIsDeliveryBySupplier;

  const haveOnlyOneWarehouse = warehouseReducer?.data?.length === 1;
  const answerSchema = useMemo(
    () => createAnswerSchema(isDelivery),
    [isDelivery]
  );
  const methods = useForm<ICreateAnswerForm>({
    mode: 'onSubmit',
    resolver: zodResolver(answerSchema),
    defaultValues: {
      pendingItems: [],
      answeredItems: []
    }
  });
  const [isLoadingGrid, setIsLoadingGrid] = useState<boolean>(false);
  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
  const [isConsultingNags, setIsConsultingNags] = useState<boolean>(false);
  const [openModalDetails, setOpenModalDetails] = useState<boolean>(false);
  const [openModalOnce, setOpenModalOnce] = useState<boolean>(true);
  const [isShowModalReasonAll, setIsShowModalReasonAll] =
    useState<boolean>(true);
  const [detailSupplierGuid, setDetailSupplierGuid] = useState<string | null>(
    null
  );
  const [openModalImages, setOpenModalImages] = useState<boolean>(false);
  const [isRequoteNational, setIsRequoteNational] = useState<boolean>(false);
  const [openModalRequoteNational, setOpenModalRequoteNational] =
    useState<boolean>(false);
  const [openModalFavoriteCategory, setOpenModalFavoriteCategory] =
    useState<boolean>(false);
  const [modalImagesState, setModalImagesState] =
    useState<IModalUploadImagesState>({
      allowEdit: false,
      isOpen: false,
      part: null
    });

  const isSupplier = userIsSupplier();

  // Async Functions
  async function loadGridPending(
    guidQuote: string,
    guidSupplier: string,
    idQuoteSuppItemStatus: number | null = null
  ) {
    try {
      const response = await quoteSupplierItemApi.getParts({
        guidQuote: guidQuote,
        guidSupplier: guidSupplier,
        ...(idQuoteSuppItemStatus && {
          idQuoteSuppItemStatus: idQuoteSuppItemStatus
        })
      });
      const result = response.data.result;
      const resultFormatted = result.map(item => ({
        ...item,
        idUnique: uniqueIdRow(item.guidQuoteSupplierItem)
      }));

      const shouldConsultNags =
        isSupplier &&
        Boolean(dataHeader?.isNagsCodeCall) &&
        resultFormatted.some(part => part.idPartType === PartTypeEnum.Glass);

      const formattedItems = resultFormatted.map(item => {
        const formatted = formatResponseToForm(
          item,
          true,
          haveOnlyOneWarehouse
        );
        if (shouldConsultNags && item.idPartType === PartTypeEnum.Glass) {
          return { ...formatted, isNagsLoading: true };
        }
        return formatted;
      });
      methods.setValue('pendingItems', formattedItems);

      setDataGridPending(resultFormatted);
      setpendingIsDeliveryBySupplier(
        result.some(item => item.deliveryBySupplier)
      );
      const requoteNational = resultFormatted.find(
        item => item.national === true
      );
      if (requoteNational) setIsRequoteNational(true);

      if (shouldConsultNags) {
        void runNagsConsult(resultFormatted);
      }
    } catch (error) { }
  }

  async function loadGridAnswer(
    guidQuote: string,
    guidSupplier: string,
    idQuoteSuppItemStatus: number | null = null
  ) {
    try {
      const response = await quoteSupplierItemApi.getParts({
        guidQuote: guidQuote,
        guidSupplier: guidSupplier,
        ...(idQuoteSuppItemStatus && {
          idQuoteSuppItemStatus: idQuoteSuppItemStatus
        })
      });
      const result = response.data.result;
      const resultFormatted = result.map(item => ({
        ...item,
        idUnique: uniqueIdRow(item.guidQuoteSupplierItem)
      }));

      const formattedItems = resultFormatted.map(item =>
        formatResponseToForm(item, false)
      );
      methods.setValue('answeredItems', formattedItems);

      setDataGridAnswer(resultFormatted);
      setansweredIsDeliveryBySupplier(
        result.some(item => item.deliveryBySupplier)
      );
    } catch (error) { }
  }

  async function loadGrid(
    guidQuote: string,
    guidSupplier: string,
    idQuoteSuppItemStatus: number | null = null
  ) {
    try {
      setIsLoadingGrid(true);
      const [responseGridPending, responseGridAnswer] = await Promise.all([
        loadGridPending(
          guidQuote,
          guidSupplier,
          StatusQuoteSupplierItem.Pending
        ),
        loadGridAnswer(
          guidQuote,
          guidSupplier,
          StatusQuoteSupplierItem.Answered
        )
      ]);
    } catch (error) {
    } finally {
      setIsLoadingGrid(false);
    }
  }

  async function runNagsConsult(
    pendingParts: QuoteSupplierItemPartResponse[]
  ) {
    const glassParts = pendingParts.filter(
      part => part.idPartType === PartTypeEnum.Glass
    );
    if (glassParts.length === 0) return;

    setIsConsultingNags(true);
    try {
      const response =
        await quoteSupplierItemApi.searchNAGSCode(guidQuoteParams);
      const nagsCodeResults = response.data.result?.nagsCodeResults ?? [];

      const priceByCodeGlass = new Map(
        nagsCodeResults
          .filter(nags => !isNullUndefinedEmpty(nags.nagsPrice))
          .map(nags => [
            String(nags.nagsCode),
            nags.nagsPrice as number
          ])
      );

      const currentPending = methods.getValues('pendingItems');
      glassParts.forEach(glassPart => {
        const pendingIndex = currentPending.findIndex(
          item => item.idUnique === glassPart.idUnique
        );
        if (pendingIndex === -1) return;
        const codeGlass = String(glassPart.quoteProcurementPartInfo?.glassInfo);
        const codeGlassFormatted = codeGlass.replace(/\[|\]/g, '');

        const nagsPrice = priceByCodeGlass.get(
          codeGlassFormatted
        );
        if (isNullUndefinedEmpty(nagsPrice)) return;

        methods.setValue(
          `pendingItems.${pendingIndex}.answeredPrice`,
          nagsPrice as number,
          { shouldDirty: true, shouldValidate: false }
        );
        methods.setValue(`pendingItems.${pendingIndex}.nagsPrice`, nagsPrice);
        methods.setValue(`pendingItems.${pendingIndex}.isNagsLocked`, true);
      });

      toast.success(getTranslateByKey('information.nagsConsultFinished'));
    } catch (error) {
    } finally {
      setIsConsultingNags(false);
      const finalPending = methods.getValues('pendingItems');
      finalPending.forEach((item, index) => {
        if (item.isNagsLoading) {
          methods.setValue(`pendingItems.${index}.isNagsLoading`, false);
        }
      });
    }
  }

  // Handle Functions

  function handleUpdateItem(
    guidQuoteSupplierItem: string,
    images: QuoteSupplierItemImageResponse[],
    itemIdUnique: string
  ) {
    setDataGridPending(prevGrid =>
      prevGrid.map(item => {
        if (item.idUnique === itemIdUnique) {
          return {
            ...item,
            images: images,
            guidQuoteSupplierItem: guidQuoteSupplierItem
          };
        }
        return item;
      })
    );

    setDataGridAnswer(prevGrid =>
      prevGrid.map(item => {
        if (item.idUnique === itemIdUnique) {
          return {
            ...item,
            images: images,
            guidQuoteSupplierItem: guidQuoteSupplierItem
          };
        }
        return item;
      })
    );

    const currentPendingItems = methods.getValues('pendingItems') || [];
    const itemIndex = currentPendingItems.findIndex(
      item => item.idUnique === itemIdUnique
    );

    if (itemIndex !== -1) {
      methods.setValue(
        `pendingItems.${itemIndex}.guidQuoteSupplierItem`,
        guidQuoteSupplierItem
      );
    }
  }

  function handleOpenModalImages(
    item: QuoteSupplierItemPartResponse,
    allowEdit: boolean
  ) {
    setModalImagesState({ part: item, allowEdit, isOpen: true });
  }

  function handleCloseModalImages() {
    setModalImagesState({ part: null, allowEdit: false, isOpen: false });
  }

  function handleCloseModalFavoriteCategory(refresh: boolean) {
    setOpenModalFavoriteCategory(false);
    if (refresh) {
      if (
        !isNullUndefinedEmpty(dataHeader.guidSupplier) &&
        !isNullUndefinedEmpty(dataHeader.idQuoteSuppItemStatus)
      ) {
        loadGrid(
          guidQuoteParams,
          dataHeader.guidSupplier,
          dataHeader.idQuoteSuppItemStatus
        );
      }
    }
  }

  function handleSave(data: ICreateAnswerForm) {
    setIsLoadingSave(true);

    const asRecord: IFormValueRowRoot = {};
    [...data.pendingItems].forEach(item => {
      asRecord[item.idUnique] = item;
    });

    const formattedRequest = generateRequestSaveCreateAnswer({
      data: asRecord,
      dataGridPending,
      guidAddress: dataHeader.guidAddress,
      idQuoteSuppItemStatus: dataHeader.idQuoteSuppItemStatus
    });

    if (formattedRequest.length === 0) {
      toast.error(getTranslateByKey('information.requiredFieldsMissing', true));
      setIsLoadingSave(false);
      return;
    }

    quoteSupplierItemApi
      .save(formattedRequest)
      .then(() => {
        toast.success(getTranslateByKey('information.savedSuccess'));
        loadHeader(guidQuoteParams);
        navigate(RoutesConstants.quotationCartPendingToAnswer);
      })
      .catch(() => {
        const pendingItems = methods.getValues('pendingItems');
        pendingItems.forEach((item, index) => {
          if (!item.notQuote) {
            methods.setError(`pendingItems.${index}.height`, {
              type: 'manual',
              message: ''
            });
            methods.setError(`pendingItems.${index}.width`, {
              type: 'manual',
              message: ''
            });
            methods.setError(`pendingItems.${index}.depth`, {
              type: 'manual',
              message: ''
            });
          }
        });
      })
      .finally(() => {
        setIsLoadingSave(false);
      });
  }

  function handleOpenModalPart(guidSupplierRequest: string) {
    setOpenModalDetails(true);
    setDetailSupplierGuid(guidSupplierRequest);
  }

  function handleBack() {
    navigate(RoutesConstants.quotationCartPendingToAnswer);
  }

  function handleOpenModalRequoteNational() {
    if (
      dataGridPending.find(x => x.national === true) &&
      dataHeader.idQuoteSuppItemStatus === StatusQuoteSupplierItem.Pending
    )
      setOpenModalRequoteNational(true);
  }

  function hasUnhandledHiddenItems(items: IFormValueRow[]): boolean {
    const isGroupedHidden = items.some(
      item => !isNil(item.isGroupHidden) && item.isGroupHidden
    );

    if (isGroupedHidden) {
      return items.some(item => item.isGroupHidden && !item.notQuote);
    }

    return items.some(item => {
      const filledFields =
        !isNullUndefinedEmpty(item.answeredPrice) ||
        !isNullUndefinedEmpty(item.pickupTime) ||
        !isNullUndefinedEmpty(item.weight) ||
        !isNullUndefinedEmpty(item.height) ||
        !isNullUndefinedEmpty(item.depth) ||
        !isNullUndefinedEmpty(item.partNumber);

      return !item.notQuote && !filledFields;
    });
  }

  function hasExcessivePickupTime(items: IFormValueRow[]): boolean {
    return items
      .filter(part => !part.isGroupHidden)
      .some(
        part =>
          parseInt(part.pickupTime, 10) >=
          DefaultValueParameterEnum.CreateAnswerPickupTimeLimit
      );
  }

  function onValidSubmit(data: ICreateAnswerForm) {
    const { pendingItems } = data;

    if (hasUnhandledHiddenItems(pendingItems)) {
      setIsOpenModalNotQuoteSubmit(true);
      return;
    }

    handleSave(data);
  }

  function isItemModifiedFromOriginal(formItem: IFormValueRow): boolean {
    const originalItem = dataGridPending.find(
      d => d.idUnique === formItem.idUnique
    );
    if (!originalItem) return true;

    const originalFormatted = formatResponseToForm(originalItem, true);

    const fieldsToCompare: (keyof IFormValueRow)[] = [
      'answeredPrice',
      'stock',
      'pickupTime',
      'weight',
      'height',
      'width',
      'depth',
      'partNumber'
    ];

    for (const field of fieldsToCompare) {
      if (
        String(formItem[field] ?? '') !== String(originalFormatted[field] ?? '')
      ) {
        return true;
      }
    }

    if (formItem.warehouseName?.key !== originalFormatted.warehouseName?.key)
      return true;
    if (formItem.partBrandName?.key !== originalFormatted.partBrandName?.key)
      return true;
    if (
      formItem.packageTypeName?.key !== originalFormatted.packageTypeName?.key
    )
      return true;
    if (formItem.notQuote !== originalFormatted.notQuote) return true;

    return false;
  }

  function showValidationToasts(
    indexedPendingErrors: IIndexedPendingItemError[]
  ) {
    toast.dismiss();
    const formValues = methods.getValues('pendingItems');

    const visiblePositionMap = new Map<string, number>();
    let visualPos = 0;
    formValues.forEach(item => {
      if (!item.isGroupHidden) {
        visualPos++;
        visiblePositionMap.set(item.idUnique, visualPos);
      }
    });

    const mappedErrors = indexedPendingErrors
      .map(({ index, error }) => {
        const errorObj = error;
        if (!errorObj) return null;

        const formItem = formValues[index];
        if (!formItem) return null;

        const gridItem = dataGridPending[index];
        if (!gridItem) return null;

        const fieldErros = Object.entries(errorObj).reduce(
          (acc: string[], [key]: any) => {
            acc.push(getLabelTranslation(key));
            return acc;
          },
          []
        );

        const partInfo = gridItem.quoteProcurementPartInfo;
        const partName = partInfo?.partName || '';
        const partNumber = partInfo?.quoteItemPartNumber || '';
        const partTypeName = gridItem.partTypeName || '';
        const lineNumber = visiblePositionMap.get(formItem.idUnique) ?? '';
        const nameParts = [partName, partNumber, partTypeName]
          .filter(Boolean)
          .join(' - ');
        const nameKey = lineNumber ? `${nameParts} (${lineNumber})` : nameParts;

        return { key: nameKey, fieldErros };
      })
      .filter(Boolean);

    mappedErrors.forEach(item => {
      if (!item) return;
      const messageError = getTranslateByKey(
        'information.fieldsInvalidToast',
        true
      )
        .replace('{0}', item.key || '')
        .replace('{1}', item.fieldErros.join(', '));
      toast.error(messageError);
    });
  }

  function onInvalidSubmit(errors: FieldErrors<ICreateAnswerForm>) {
    const indexedPendingErrors = getIndexedPendingItemErrors(errors);

    if (indexedPendingErrors.length === 0) return;

    const pendingItems = methods.getValues('pendingItems');

    const modifiedErrors = indexedPendingErrors.filter(({ index }) => {
      const formItem = pendingItems[index];
      return formItem && isItemModifiedFromOriginal(formItem);
    });

    if (modifiedErrors.length > 0) {
      const modifiedIndexes = new Set(modifiedErrors.map(e => e.index));
      indexedPendingErrors.forEach(({ index }) => {
        if (!modifiedIndexes.has(index)) {
          methods.clearErrors(`pendingItems.${index}`);
        }
      });
      showValidationToasts(modifiedErrors);
      return;
    }

    methods.clearErrors();
    setIsOpenModalNotQuoteSubmit(true);
  }

  async function handleSubmit() {
    if (isConsultingNags) return;
    if (hasExcessivePickupTime(methods.getValues('pendingItems'))) {
      const isConfirmModalDeadline = await getConfirmation(
        getTranslateByKey('information.longPickupDays')
      );
      if (!isConfirmModalDeadline) return;
    }

    methods.handleSubmit(onValidSubmit, onInvalidSubmit)();
  }

  useEffect(() => {
    if (guidQuoteParams && brandReducer.data === null) dispatch(getBrand());
  }, [guidQuoteParams]);

  useEffect(() => {
    if (warehouseReducer?.data?.length !== 1) return;
    const warehouse = warehouseReducer.data[0];
    const pendingItems = methods.getValues('pendingItems');
    if (pendingItems.length === 0) return;
    pendingItems.forEach((item, index) => {
      if (!item.warehouseName) {
        methods.setValue(`pendingItems.${index}.warehouseName`, warehouse);
        methods.setValue(
          `pendingItems.${index}.validateWhenOnlyOneWarehouse`,
          false
        );
      }
    });
  }, [warehouseReducer?.data]);

  // Monitor changes to pending items using useWatch for reactivity
  const pendingItemsWatch = useWatch({
    name: 'pendingItems',
    control: methods.control,
    defaultValue: []
  });

  useEffect(() => {
    if (!haveOnlyOneWarehouse) return;
    if (!pendingItemsWatch || pendingItemsWatch.length === 0) return;

    const fieldsToWatch = [
      'partNumber',
      'partBrandName',
      'stock',
      'answeredPrice',
      'pickupTime',
      'height',
      'width',
      'depth',
      'weight'
    ];

    pendingItemsWatch.forEach((item: IFormValueRow, index: number) => {
      if (!item || !item.idUnique) return;

      const originalItem = dataGridPending.find(
        d => d.idUnique === item.idUnique
      );
      if (!originalItem) return;

      const originalFormatted = formatResponseToForm(originalItem, true);

      const hasChanges = fieldsToWatch.some(field => {
        const currentValue = item[field as keyof IFormValueRow];
        const originalValue =
          originalFormatted[field as keyof typeof originalFormatted];
        return String(currentValue ?? '') !== String(originalValue ?? '');
      });

      if (item.validateWhenOnlyOneWarehouse !== hasChanges) {
        methods.setValue(
          `pendingItems.${index}.validateWhenOnlyOneWarehouse`,
          hasChanges,
          { shouldDirty: false, shouldValidate: false }
        );
      }
    });
  }, [pendingItemsWatch, dataGridPending, haveOnlyOneWarehouse]);

  useEffect(() => {
    if (
      !isNullUndefinedEmpty(dataHeader.guidSupplier) &&
      !isNullUndefinedEmpty(dataHeader.idQuoteSuppItemStatus)
    ) {
      loadGrid(
        guidQuoteParams,
        dataHeader.guidSupplier,
        dataHeader.idQuoteSuppItemStatus
      );
      dispatch(getWarehouse({ guidSupplier: dataHeader.guidSupplier }));
    }
  }, [dataHeader]);

  useEffect(() => {
    if (dataHeader?.guidCountry) {
      if (packageReducer.data === null) {
        dispatch(getPackage({ guidCountry: String(dataHeader.guidCountry) }));
      }

      const requestReason: ReasonFilterRequest = {
        guidCountry: dataHeader.guidCountry,
        idReasonType: ReasonTypeEnum.NotQuote
      };
      dispatch(getReason(requestReason));
    }
  }, [dataHeader.guidCountry]);

  useEffect(() => {
    if (dataGridPending.find(x => x.national === true))
      handleOpenModalRequoteNational();
  }, [dataGridPending]);

  // Constants Layout
  const isStatusAnswered =
    dataHeader.idQuoteSuppItemStatus === StatusQuoteSupplierItem.Answered;

  const showSectionPending =
    !isFinishedTime && !isLoadingHeader && !isNullUndefinedEmpty(dataHeader);
  const showSectionFavoriteCategory =
    showSectionPending && dataGridPending.some(item => item.isFavorite);
  const showSectionAnswer =
    !isLoadingHeader && !isNullUndefinedEmpty(dataHeader);

  return (
    <ContainerScreen title={titleScreen}>
      <AreaButton>
        <SecondaryButton
          id="id-createAnswerCarts-btn-favoriteCategory"
          type="button"
          onClick={() => setOpenModalFavoriteCategory(true)}>
          {getTranslateByKey('information.myFavoriteCategory')}
        </SecondaryButton>
        <SecondaryButton
          id="id-createAnswerCarts-btn-images"
          type="button"
          onClick={() => setOpenModalImages(true)}>
          {getTranslateByKey('information.images')} /{' '}
          {getTranslateByKey('information.files')}
        </SecondaryButton>
      </AreaButton>
      <FormProvider {...methods}>
        <S.Container>
          <HeaderAnswer
            code={dataHeader.code}
            claimNr={dataHeader.claimNumber}
            insurerName={dataHeader.insurerCompanyName}
            bodyshopName={dataHeader.bodyShopCompanyName}
            bodyshopContactName={dataHeader.bodyshopContactName}
            bodyshopPhoneNr={dataHeader.bodyshopPhoneNr}
            bodyshopEmail={dataHeader.bodyshopEmail}
            vehicle={dataHeader.vehicle}
            vin={dataHeader.vin}
            licensePlate={dataHeader.vehicleRegistration}
            deliveryAddress={dataHeader.deliveryAddress}
            isLoading={isLoadingHeader}
            deadlineAnswer={dataHeader.deadlineAnswer}
            handleFinishTime={handleFinishTime}
            isFinishedTime={isFinishedTime}
            guidQuote={guidQuoteParams}
            showRemainingTimeAllStatuses
            status={{
              id: dataHeader.idQuoteSuppItemStatus,
              name: dataHeader.statusName
            }}
          />

          {showSectionFavoriteCategory && (
            <GridPending
              hasReadOnly={false}
              idStatus={StatusQuoteSupplierItem.Pending}
              dataGrid={dataGridPending}
              dataHeader={dataHeader}
              isFinishedTime={isFinishedTime}
              loading={isLoadingGrid}
              handleOpenModalDetails={handleOpenModalPart}
              openModalOnce={openModalOnce}
              isShowModalReasonAll={isShowModalReasonAll}
              setIsShowModalReasonAll={setIsShowModalReasonAll}
              setOpenModalOnce={setOpenModalOnce}
              updateDataGrid={data => setDataGridPending(data)}
              isDeliveryBySupplier={
                pendingIsDeliveryBySupplier || answeredIsDeliveryBySupplier
              }
              isRequoteNational={isRequoteNational}
              filterByFavorite={true}
              cardTitle={getTranslateByKey('information.favorites')}
              isOpenInitial={true}
              handleOpenModalImages={handleOpenModalImages}
            />
          )}

          {showSectionPending && (
            <GridPending
              hasReadOnly={false}
              loading={isLoadingGrid}
              idStatus={StatusQuoteSupplierItem.Pending}
              dataGrid={dataGridPending}
              dataHeader={dataHeader}
              isFinishedTime={isFinishedTime}
              handleOpenModalDetails={handleOpenModalPart}
              openModalOnce={openModalOnce}
              isShowModalReasonAll={isShowModalReasonAll}
              setIsShowModalReasonAll={setIsShowModalReasonAll}
              setOpenModalOnce={setOpenModalOnce}
              updateDataGrid={data => setDataGridPending(data)}
              isDeliveryBySupplier={
                pendingIsDeliveryBySupplier || answeredIsDeliveryBySupplier
              }
              isRequoteNational={isRequoteNational}
              filterByFavorite={false}
              isOpenInitial={true}
              handleOpenModalImages={handleOpenModalImages}
            />
          )}

          {showSectionAnswer && (
            <GridAnswer
              hasReadOnly={true}
              dataHeader={dataHeader}
              loading={isLoadingGrid}
              idStatus={StatusQuoteSupplierItem.Answered}
              dataGrid={dataGridAnswer}
              isFinishedTime={isFinishedTime}
              handleOpenModalDetails={handleOpenModalPart}
              openModalOnce={openModalOnce}
              isShowModalReasonAll={isShowModalReasonAll}
              setIsShowModalReasonAll={setIsShowModalReasonAll}
              loadGrid={loadGrid}
              setOpenModalOnce={setOpenModalOnce}
              updateDataGrid={data => setDataGridPending(data)}
              isDeliveryBySupplier={
                pendingIsDeliveryBySupplier || answeredIsDeliveryBySupplier
              }
              isOpenInitial={false}
              handleOpenModalImages={handleOpenModalImages}
            />
          )}

          <S.FooterButtons>
            <SecondaryButton
              id="id-createAnswerCarts-btn-back"
              type="button"
              onClick={handleBack}>
              {getTranslateByKey('information.back')}
            </SecondaryButton>
            {!isStatusAnswered && (
              <Button
                id="id-createAnswerCarts-btn-save"
                type="submit"
                onClick={handleSubmit}
                isLoading={isLoadingSave}
                disabled={isFinishedTime || isLoadingSave || isConsultingNags}>
                {getTranslateByKey('information.save')}
              </Button>
            )}
          </S.FooterButtons>

          <ModalImages
            isOpenModal={openModalImages}
            closeModal={() => setOpenModalImages(false)}
            setIsOpenModal={setOpenModalImages}
            canUploadImages={!isSupplier}
            canShowPDF={!isSupplier}
            guidQuote={guidQuoteParams}
            removeDeleteButton
            showButtonImagesNewTab
            canDownloadImages={useKogControlElementId(
              Controls.IdDownloadImagesAnsweredCartControl
            )}
          />

          <ModalUploadImage
            isOpen={modalImagesState.isOpen}
            quoteSupplierItemPart={modalImagesState.part}
            loadedImages={modalImagesState.part?.images}
            allowEdit={modalImagesState.allowEdit}
            handleClose={handleCloseModalImages}
            updateItem={handleUpdateItem}
          />

          <KogModalSideComponent
            titleModalReducer="modalDetails"
            isClosedOtherModal={false}
            open={openModalDetails}
            closeModal={() => {
              setOpenModalDetails(false);
              setDetailSupplierGuid(null);
            }}>
            {openModalDetails && (
              <ModalPartDetails
                closeModal={() => {
                  setOpenModalDetails(false);
                  setDetailSupplierGuid(null);
                }}
                guid={detailSupplierGuid}
              />
            )}
          </KogModalSideComponent>

          <KogModalFadeComponent
            titleModalReducer="modalRequoteNational"
            open={openModalRequoteNational}
            setOpen={setOpenModalRequoteNational}>
            <ModalNationalRequote
              titleModal={getTranslateByKey(
                'information.nationalRequoteWarningModal'
              )}
              handleClose={() => {
                setOpenModalRequoteNational(false);
              }}
            />
          </KogModalFadeComponent>
          <ModalNotQuoteSubmit
            isOpenModal={isOpenModalNotQuoteSubmit}
            handleSave={handleSave}
            setIsOpenModal={setIsOpenModalNotQuoteSubmit}
            closeModal={() => {
              methods.clearErrors();
              setIsOpenModalNotQuoteSubmit(false);
            }}
          />

          <ModalConfirmation />

          <ModalFavoriteCategory
            open={openModalFavoriteCategory}
            onClose={handleCloseModalFavoriteCategory}
          />
        </S.Container>
      </FormProvider>
    </ContainerScreen>
  );
}

export default CreateAnswerCartsScreen;
